/**
 * Iter 64 — TUS resumable upload hook.
 *
 * Wraps tus-js-client and exposes per-file progress, pause/resume/cancel, and
 * retry-on-failure for the Submissions upload modal.
 *
 * Concurrency: at most 2 active uploads at a time (next file in the queue
 * starts only when one of the in-flight uploads finishes/cancels).
 *
 * State per file:
 *   { id, file, sessionId, total, offset, status, error, etaSec, speedBps,
 *     pause, resume, cancel }
 *
 * status ∈ 'queued' | 'starting' | 'uploading' | 'paused' | 'success' | 'error' | 'cancelled'
 */
import * as tus from 'tus-js-client';
import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_CONCURRENT = 2;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB chunks per spec

// 2026-02 — Chunk-level retry policy aligned with the claim-tus client
// retry wrapper. Retry ONLY transient gateway/network classes; pass
// permanent errors (400/401/403/404/422) straight to onError so we don't
// burn 37 s pointlessly on a real bug. tus-js-client calls this for every
// failed request (POST create, PATCH chunk, HEAD resume) BEFORE applying
// retryDelays.
const RETRYABLE_TUS_STATUSES = new Set([0, 423, 500, 502, 503, 504]);
function shouldRetryTusError(err) {
  const status = err?.originalResponse?.getStatus?.() ?? 0;
  // Network-level error (TypeError, no response) → status will be 0/undefined.
  if (!status) return true;
  return RETRYABLE_TUS_STATUSES.has(status);
}

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useTusUploads({ workspaceId, taskId }) {
  // Items keyed by id. We DO NOT store the tus.Upload instance in React
  // state (it isn't serializable + would cause re-render storms on progress).
  // It lives in a ref keyed by id. Public state mirrors only the UI fields.
  const uploadRefs = useRef(new Map()); // id -> tus.Upload
  const speedRefs = useRef(new Map());  // id -> { lastTs, lastOffset, ema }
  const [items, setItems] = useState([]);

  const patch = useCallback((id, partial) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...partial } : it)));
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    uploadRefs.current.delete(id);
    speedRefs.current.delete(id);
  }, []);

  const startTusUpload = useCallback((id, file, opts) => {
    const token = localStorage.getItem('stellar_token');
    const endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/tus`;
    const upload = new tus.Upload(file, {
      endpoint,
      chunkSize: CHUNK_SIZE,
      // 2026-02 — Retry only transient gateway/network errors. Backoffs:
      // 0, 2s, 5s, 10s, 20s (≤5 attempts per chunk, ~37s worst case).
      retryDelays: [0, 2000, 5000, 10000, 20000],
      onShouldRetry: shouldRetryTusError,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      metadata: {
        filename: file.name,
        filetype: file.type || 'application/octet-stream',
        mime_type: file.type || 'application/octet-stream',
        workspace_id: workspaceId,
        task_id: taskId,
      },
      // Persist upload URL in localStorage so a tab reload can resume
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onError: (err) => {
        // Log + surface to UI; tus-js-client has already exhausted retries
        // before invoking this. Keep staging dir so user can hit "Try again".
        // eslint-disable-next-line no-console
        console.error('[tus] upload error', file.name, err);
        patch(id, { status: 'error', error: err.message || 'Upload failed' });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        // Track rolling EMA upload speed for ETA
        const now = Date.now();
        const prev = speedRefs.current.get(id) || { lastTs: now, lastOffset: 0, ema: 0 };
        const dt = (now - prev.lastTs) / 1000;
        let speedBps = prev.ema;
        if (dt >= 0.25) {
          const inst = (bytesUploaded - prev.lastOffset) / Math.max(dt, 0.001);
          speedBps = prev.ema ? 0.7 * prev.ema + 0.3 * inst : inst;
          speedRefs.current.set(id, { lastTs: now, lastOffset: bytesUploaded, ema: speedBps });
        }
        const remaining = bytesTotal - bytesUploaded;
        const etaSec = speedBps > 1024 ? Math.max(1, Math.round(remaining / speedBps)) : null;
        patch(id, {
          offset: bytesUploaded,
          total: bytesTotal,
          speedBps,
          etaSec,
          status: 'uploading',
        });
      },
      onSuccess: () => {
        // Pull session_id out of the upload URL the server gave us
        const url = upload.url || '';
        const sessionId = url.split('/').filter(Boolean).pop();
        patch(id, { status: 'success', sessionId, offset: file.size, total: file.size });
        opts?.onComplete?.({ id, sessionId, file });
      },
    });
    uploadRefs.current.set(id, upload);
    upload.start();
  }, [patch, workspaceId, taskId]);

  const startResumeFromUrl = useCallback((id, file, uploadUrl, opts) => {
    const token = localStorage.getItem('stellar_token');
    const endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/tus`;
    const upload = new tus.Upload(file, {
      endpoint,
      uploadUrl,
      chunkSize: CHUNK_SIZE,
      retryDelays: [0, 2000, 5000, 10000, 20000],
      onShouldRetry: shouldRetryTusError,
      headers: { Authorization: `Bearer ${token}` },
      metadata: {
        filename: file.name,
        filetype: file.type || 'application/octet-stream',
        mime_type: file.type || 'application/octet-stream',
        workspace_id: workspaceId,
        task_id: taskId,
      },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.error('[tus] resume error', file.name, err);
        patch(id, { status: 'error', error: err.message || 'Resume failed' });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const now = Date.now();
        const prev = speedRefs.current.get(id) || { lastTs: now, lastOffset: bytesUploaded, ema: 0 };
        const dt = (now - prev.lastTs) / 1000;
        let speedBps = prev.ema;
        if (dt >= 0.25) {
          const inst = (bytesUploaded - prev.lastOffset) / Math.max(dt, 0.001);
          speedBps = prev.ema ? 0.7 * prev.ema + 0.3 * inst : inst;
          speedRefs.current.set(id, { lastTs: now, lastOffset: bytesUploaded, ema: speedBps });
        }
        const etaSec = speedBps > 1024 ? Math.max(1, Math.round((bytesTotal - bytesUploaded) / speedBps)) : null;
        patch(id, { offset: bytesUploaded, total: bytesTotal, speedBps, etaSec, status: 'uploading' });
      },
      onSuccess: () => {
        const url = upload.url || uploadUrl;
        const sessionId = url.split('/').filter(Boolean).pop();
        patch(id, { status: 'success', sessionId, offset: file.size, total: file.size });
        opts?.onComplete?.({ id, sessionId, file });
      },
    });
    uploadRefs.current.set(id, upload);
    upload.start();
  }, [patch, workspaceId, taskId]);

  // Concurrency-limited starter: pick up queued items as slots free up
  const promoteQueue = useCallback(() => {
    setItems((current) => {
      const active = current.filter(
        (it) => it.status === 'starting' || it.status === 'uploading'
      ).length;
      let toPromote = MAX_CONCURRENT - active;
      if (toPromote <= 0) return current;
      let mutated = false;
      const next = current.map((it) => {
        if (toPromote > 0 && it.status === 'queued') {
          toPromote -= 1;
          mutated = true;
          // Defer the actual tus.Upload creation to next tick so we don't
          // mutate refs inside setState callback.
          setTimeout(() => {
            const ref = uploadRefs.current.get(it.id);
            if (!ref) startTusUpload(it.id, it.file, { onComplete: it._onComplete });
          }, 0);
          return { ...it, status: 'starting' };
        }
        return it;
      });
      return mutated ? next : current;
    });
  }, [startTusUpload]);

  // Listen for terminal status changes to promote next queued item
  useEffect(() => {
    promoteQueue();
  }, [items, promoteQueue]);

  const enqueue = useCallback((files, onComplete) => {
    const newItems = files.map((file) => ({
      id: uid(),
      file,
      sessionId: null,
      total: file.size,
      offset: 0,
      status: 'queued',
      error: null,
      etaSec: null,
      speedBps: 0,
      _onComplete: onComplete,
    }));
    setItems((prev) => [...prev, ...newItems]);
    return newItems.map((i) => i.id);
  }, []);

  const enqueueResume = useCallback((file, uploadUrl, onComplete) => {
    const id = uid();
    setItems((prev) => [...prev, {
      id, file, sessionId: null, total: file.size, offset: 0,
      status: 'starting', error: null, etaSec: null, speedBps: 0,
      _onComplete: onComplete,
    }]);
    setTimeout(() => startResumeFromUrl(id, file, uploadUrl, { onComplete }), 0);
    return id;
  }, [startResumeFromUrl]);

  const pause = useCallback((id) => {
    const u = uploadRefs.current.get(id);
    if (u) {
      u.abort();
      patch(id, { status: 'paused' });
    }
  }, [patch]);

  const resume = useCallback((id) => {
    const u = uploadRefs.current.get(id);
    if (u) {
      patch(id, { status: 'uploading' });
      u.start();
    }
  }, [patch]);

  const cancel = useCallback(async (id) => {
    const u = uploadRefs.current.get(id);
    if (u) {
      // abort(true) will also DELETE the server-side session
      try { await u.abort(true); } catch { /* ignore */ }
    }
    patch(id, { status: 'cancelled' });
    uploadRefs.current.delete(id);
  }, [patch]);

  const retry = useCallback((id) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    // Drop the failed instance and re-queue the file as fresh
    uploadRefs.current.delete(id);
    speedRefs.current.delete(id);
    patch(id, { status: 'queued', error: null, offset: 0, etaSec: null, speedBps: 0 });
  }, [items, patch]);

  const reset = useCallback(() => {
    // Abort everything currently in flight, drop all state
    for (const u of uploadRefs.current.values()) {
      try { u.abort(); } catch { /* ignore */ }
    }
    uploadRefs.current.clear();
    speedRefs.current.clear();
    setItems([]);
  }, []);

  return {
    items,
    enqueue,
    enqueueResume,
    pause,
    resume,
    cancel,
    retry,
    remove,
    reset,
  };
}
